import React from 'react';
import styled from 'styled-components';
import {
  Badge,
  Button,
  Modal,
  Notice,
  Spinner,
  Text,
} from '@companycam/slab-web';
import { Steven } from 'helpers/steven';

import PromoModalVideoPlayer from 'components/shared/PromoModalVideoPlayer';
import { translate } from 'javascripts/translations/i18n';
import useRoleSafeBillingQuery from 'components/billing/v2/useRoleSafeBillingQuery';
import { useModal } from './hooks';

const StyledBadge = styled(Badge)`
  width: fit-content;
`;

const TitleContainer = styled.div`
  display: inline-block;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-bottom: var(--cc_size_spacing_m);
`;

const ContentContainer = styled.div`
  padding: var(--cc_size_spacing_xl) var(--cc_size_spacing_xxl);
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_s);
`;

const FeatureListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_m);
`;

const FeatureContainer = styled.div`
  display: flex;
  gap: var(--cc_size_spacing_m);
  align-items: center;
`;

const FeatureIcon = styled.i`
  color: var(--cc_color_text_nonessential);
`;

const UpgradeNotice = styled(Notice)`
  margin-top: var(--cc_size_spacing_xl);
`;

const PromoModal = ({
  onRequestClose,
  videoUrl,
  titleText,
  messageText,
  features,
  upgradeNoticeText,
  dataTestId,
  showAdminNotice,
  trackingAction,
}: {
  onRequestClose: () => void;
  videoUrl?: string;
  titleText: string;
  messageText?: string;
  features: string[];
  upgradeNoticeText?: string;
  showAdminNotice?: boolean;
  dataTestId?: string;
  trackingAction?: string;
}) => {
  const { loading, sampleEligible } = useRoleSafeBillingQuery();
  const { Modal: ModalKeys, showModal } = useModal();

  const onClickUpgrade = () => {
    window.location.href = '/billing/plans';
  };

  const logClickEvent = () => {
    if (trackingAction) {
      Steven.elementClicked({
        id: 'promo_modal_upgrade',
        content: sampleEligible
          ? 'valueProps.shared.tryFree'
          : 'common.upgrade',
        type: 'link',
        action: trackingAction,
      });
    }
  };

  const primaryButtonAction = () => {
    logClickEvent();
    if (sampleEligible) {
      onRequestClose();
      showModal(ModalKeys.SAMPLES_UPGRADE_MODAL);
    } else {
      onClickUpgrade();
    }
  };

  return (
    <Modal
      isOpen
      size="small"
      bodyStyle={{ padding: 0 }}
      onClose={onRequestClose}
      title=""
      headerComponent={null}
      footerActions={
        <>
          <Button color="subtle" onClick={onRequestClose}>
            {translate('common.notNow')}
          </Button>
          {loading ? (
            <Spinner accessibilityLabel="common.loading" />
          ) : (
            <Button color="primary" onClick={primaryButtonAction}>
              {translate(
                sampleEligible ? 'valueProps.shared.tryFree' : 'common.upgrade',
              )}
            </Button>
          )}
        </>
      }
    >
      <>
        {videoUrl ? <PromoModalVideoPlayer youtubeSrc={videoUrl} /> : null}
        <ContentContainer
          data-testid={dataTestId ? `promo_modal__${dataTestId}` : undefined}
        >
          <StyledBadge color="upgrade" iconName="lightning-bolt-circle">
            {translate('common.upgrade')}
          </StyledBadge>
          <TitleContainer>
            <Text
              family="heading"
              weight="800"
              as="h1"
              size="xxxl"
              ccMargin="0 0 s 0"
            >
              {titleText}
            </Text>

            <Text as="p" size="m">
              {messageText}
            </Text>
          </TitleContainer>
          <FeatureListContainer>
            {features.map((feature) => (
              <FeatureContainer key={feature}>
                <FeatureIcon className="mdi mdi-check" />
                <Text as="p" size="m" ccMargin="none">
                  {feature}
                </Text>
              </FeatureContainer>
            ))}
          </FeatureListContainer>
          {showAdminNotice ? (
            <UpgradeNotice color="info">
              <Text as="p" size="xs">
                {upgradeNoticeText}
              </Text>
            </UpgradeNotice>
          ) : null}
        </ContentContainer>
      </>
    </Modal>
  );
};

export default PromoModal;
